import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { Check, Loader2, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import { useCommunity } from '@/hooks/use-community';
import type {
  MembershipFeeKind,
  MembershipMemberType,
  MembershipPaymentDeclaration,
} from '@/types/membership';
import {
  FEE_KIND_LABELS,
  formatMembershipPrice,
  getMembershipExpiryLabel,
  ISSUED_MEMBERSHIP_STATUS_LABELS,
  MEMBER_TYPE_LABELS,
  PAYMENT_DECLARATION_STATUS_LABELS,
  PAYMENT_MODE_LABELS,
} from '@/types/membership';

type MembershipAdminResponse = {
  issuedMemberships: Array<{
    id: string;
    userId: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    memberTypeId: string;
    selectedFeeKind: MembershipFeeKind;
    status: keyof typeof ISSUED_MEMBERSHIP_STATUS_LABELS;
    membershipStartDate?: string | null;
    membershipEndDate?: string | null;
    isLifetime: boolean;
    notes?: string | null;
    paymentDeclarations: MembershipPaymentDeclaration[];
  }>;
};

type MembershipMyResponse = {
  feeStructure: {
    memberTypes: MembershipMemberType[];
  } | null;
};

export default function MembershipAdminPage() {
  const { communityId } = useParams<{ communityId: string }>();
  const { community: communityPayload } = useCommunity(communityId);
  const community = communityPayload?.community;

  const [issueUserId, setIssueUserId] = useState('');
  const [issueMemberTypeId, setIssueMemberTypeId] = useState('');
  const [issueFeeKind, setIssueFeeKind] = useState<MembershipFeeKind>('MEMBERSHIP');
  const [issueJoinDate, setIssueJoinDate] = useState(new Date().toISOString().slice(0, 10));
  const [isIssuing, setIsIssuing] = useState(false);

  const { data, mutate, isLoading } = useSWR<MembershipAdminResponse>(
    community?.isMembershipManagementEnabled && communityId
      ? `/api/communities/${communityId}/membership/issued-memberships`
      : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data)
  );

  const { data: configData } = useSWR<MembershipMyResponse>(
    community?.isMembershipManagementEnabled && communityId
      ? `/api/communities/${communityId}/membership/my`
      : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data)
  );

  const memberTypes = useMemo(
    () => configData?.feeStructure?.memberTypes ?? [],
    [configData?.feeStructure?.memberTypes]
  );
  const memberTypeMap = useMemo(
    () => new Map(memberTypes.map((memberType) => [memberType.id, memberType])),
    [memberTypes]
  );

  const issueMembership = async () => {
    if (!communityId || !issueUserId || !issueMemberTypeId) {
      toast.error('User ID and member type are required');
      return;
    }

    setIsIssuing(true);
    try {
      await axiosInstance.post(`/api/communities/${communityId}/membership/issued-memberships`, {
        userId: issueUserId,
        memberTypeId: issueMemberTypeId,
        selectedFeeKind: issueFeeKind,
        installmentPackageId: null,
        joinDate: issueJoinDate,
        membershipStartDate: issueJoinDate,
        membershipEndDate: null,
        notes: null,
      });
      toast.success('Issued membership created');
      setIssueUserId('');
      await mutate();
    } catch (error) {
      const maybeResponse = error as { response?: { data?: { error?: string } } };
      toast.error(maybeResponse.response?.data?.error ?? 'Failed to issue membership');
    } finally {
      setIsIssuing(false);
    }
  };

  if (!communityId) return null;

  if (!community?.isMembershipManagementEnabled) {
    return (
      <div className="container mx-auto py-16 px-4 text-center text-muted-foreground">
        Membership management is not enabled.
      </div>
    );
  }

  return (
    <div className="container mx-auto py-10 px-4 max-w-5xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Issued Memberships</h1>
        <p className="text-muted-foreground mt-2">
          Issue memberships, review declarations, and set active or lifetime states.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Issue Membership</CardTitle>
          <CardDescription>Create a membership record for a community member.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>User ID</Label>
            <Input value={issueUserId} onChange={(e) => setIssueUserId(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Join date</Label>
            <Input
              type="date"
              value={issueJoinDate}
              onChange={(e) => setIssueJoinDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Member type</Label>
            <Select value={issueMemberTypeId} onValueChange={setIssueMemberTypeId}>
              <SelectTrigger>
                <SelectValue placeholder="Select member type" />
              </SelectTrigger>
              <SelectContent>
                {memberTypes.map((memberType) => (
                  <SelectItem key={memberType.id} value={memberType.id}>
                    {memberType.name || MEMBER_TYPE_LABELS[memberType.feeType]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Fee bucket</Label>
            <Select
              value={issueFeeKind}
              onValueChange={(value) => setIssueFeeKind(value as MembershipFeeKind)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(FEE_KIND_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Button onClick={issueMembership} disabled={isIssuing}>
              {isIssuing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Issue membership
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Membership Review</CardTitle>
          <CardDescription>
            {data?.issuedMemberships.length ?? 0} membership
            {(data?.issuedMemberships.length ?? 0) === 1 ? '' : 's'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (data?.issuedMemberships.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No memberships yet.</p>
          ) : (
            <div className="space-y-4">
              {data?.issuedMemberships.map((membership) => (
                <MembershipReviewCard
                  key={membership.id}
                  communityId={communityId}
                  membership={membership}
                  memberType={memberTypeMap.get(membership.memberTypeId) ?? null}
                  onMutate={mutate}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MembershipReviewCard({
  communityId,
  membership,
  memberType,
  onMutate,
}: {
  communityId: string;
  membership: MembershipAdminResponse['issuedMemberships'][number];
  memberType: MembershipMemberType | null;
  onMutate: () => Promise<unknown>;
}) {
  const [status, setStatus] = useState(membership.status);
  const [membershipStartDate, setMembershipStartDate] = useState(
    membership.membershipStartDate?.slice(0, 10) ?? ''
  );
  const [membershipEndDate, setMembershipEndDate] = useState(
    membership.membershipEndDate?.slice(0, 10) ?? ''
  );
  const [notes, setNotes] = useState(membership.notes ?? '');
  const [isSaving, setIsSaving] = useState(false);

  const saveMembership = async () => {
    setIsSaving(true);
    try {
      await axiosInstance.patch(
        `/api/communities/${communityId}/membership/issued-memberships/${membership.id}`,
        {
          status,
          membershipStartDate: membershipStartDate || null,
          membershipEndDate: membership.isLifetime ? null : membershipEndDate || null,
          notes: notes || null,
          isLifetime: membership.selectedFeeKind === 'ONE_TIME',
        }
      );
      toast.success('Membership updated');
      await onMutate();
    } catch (error) {
      const maybeResponse = error as { response?: { data?: { error?: string } } };
      toast.error(maybeResponse.response?.data?.error ?? 'Failed to update membership');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border bg-card shadow-sm px-6 py-5 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-semibold">
            {[membership.firstName, membership.lastName].filter(Boolean).join(' ') ||
              membership.userId}
          </p>
          <p className="text-sm text-muted-foreground">{membership.email ?? membership.userId}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge>{ISSUED_MEMBERSHIP_STATUS_LABELS[membership.status]}</Badge>
          <Badge variant="secondary">{FEE_KIND_LABELS[membership.selectedFeeKind]}</Badge>
          {memberType && (
            <Badge variant="outline">
              {memberType.name || MEMBER_TYPE_LABELS[memberType.feeType]}
            </Badge>
          )}
          {membership.isLifetime && <Badge variant="outline">Lifetime</Badge>}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={status} onValueChange={(value) => setStatus(value as typeof status)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(ISSUED_MEMBERSHIP_STATUS_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Membership start</Label>
          <Input
            type="date"
            value={membershipStartDate}
            onChange={(e) => setMembershipStartDate(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label>Membership end</Label>
          <Input
            type="date"
            value={membershipEndDate}
            onChange={(e) => setMembershipEndDate(e.target.value)}
            disabled={membership.isLifetime}
          />
        </div>
      </div>

      {membership.membershipEndDate && (
        <p className="text-sm text-muted-foreground">
          {getMembershipExpiryLabel(membership.membershipEndDate)}
        </p>
      )}

      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </div>

      <div className="space-y-3">
        <p className="font-medium text-sm">Payment declarations</p>
        {membership.paymentDeclarations.length === 0 ? (
          <p className="text-sm text-muted-foreground">No declarations yet.</p>
        ) : (
          membership.paymentDeclarations.map((declaration) => (
            <DeclarationReviewCard
              key={declaration.id}
              communityId={communityId}
              declaration={declaration}
              onMutate={onMutate}
            />
          ))
        )}
      </div>

      <Button onClick={saveMembership} disabled={isSaving}>
        {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Save membership
      </Button>
    </div>
  );
}

function DeclarationReviewCard({
  communityId,
  declaration,
  onMutate,
}: {
  communityId: string;
  declaration: MembershipPaymentDeclaration;
  onMutate: () => Promise<unknown>;
}) {
  const [amountAccepted, setAmountAccepted] = useState(
    String(declaration.amountAccepted ?? declaration.amountDeclared)
  );
  const [notes, setNotes] = useState(declaration.notes ?? '');
  const [isSaving, setIsSaving] = useState(false);

  const updateDeclaration = async (status: keyof typeof PAYMENT_DECLARATION_STATUS_LABELS) => {
    setIsSaving(true);
    try {
      const numericAccepted = Number(amountAccepted) || 0;
      await axiosInstance.patch(
        `/api/communities/${communityId}/membership/payment-declarations/${declaration.id}`,
        {
          amountAccepted: numericAccepted,
          status,
          notes: notes || null,
        }
      );
      toast.success('Declaration updated');
      await onMutate();
    } catch (error) {
      const maybeResponse = error as { response?: { data?: { error?: string } } };
      toast.error(maybeResponse.response?.data?.error ?? 'Failed to update declaration');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="rounded-lg border p-3 space-y-3">
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline">{PAYMENT_DECLARATION_STATUS_LABELS[declaration.status]}</Badge>
        <Badge>{PAYMENT_MODE_LABELS[declaration.paymentMode]}</Badge>
      </div>
      <p className="text-sm text-muted-foreground">
        Declared {formatMembershipPrice(declaration.amountDeclared)} on{' '}
        {new Date(declaration.declaredAt).toLocaleString()}
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Accepted amount</Label>
          <Input
            type="number"
            min={0}
            value={amountAccepted}
            onChange={(e) => setAmountAccepted(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label>Notes</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        Current balance: {formatMembershipPrice(declaration.outstandingBalance)}
      </p>
      <div className="flex gap-2">
        <Button size="sm" disabled={isSaving} onClick={() => updateDeclaration('VERIFIED')}>
          <Check className="mr-2 h-4 w-4" />
          Verify
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={isSaving}
          onClick={() => updateDeclaration('PARTIALLY_VERIFIED')}
        >
          Partial
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={isSaving}
          onClick={() => updateDeclaration('REJECTED')}
        >
          <X className="mr-2 h-4 w-4" />
          Reject
        </Button>
      </div>
    </div>
  );
}
